/**
 * IndexedDB Storage Adapter — implements StorageAdapter for web using the idb library.
 */
import { openDB, type IDBPDatabase } from 'idb';
import type { StorageAdapter } from '@efb/shared-core';

const DB_NAME = 'efb-web-store';
const DB_VERSION = 1;
const STORE_NAME = 'kv';

export class IndexedDBStorageAdapter implements StorageAdapter {
  private dbPromise: Promise<IDBPDatabase> | null = null;

  private getDb(): Promise<IDBPDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME);
          }
        },
      });
    }
    return this.dbPromise;
  }

  async get<T>(key: string): Promise<T | null> {
    const db = await this.getDb();
    const val = await db.get(STORE_NAME, key);
    return val ?? null;
  }

  async set<T>(key: string, value: T): Promise<void> {
    const db = await this.getDb();
    await db.put(STORE_NAME, value, key);
  }

  async remove(key: string): Promise<void> {
    const db = await this.getDb();
    await db.delete(STORE_NAME, key);
  }

  async keys(prefix?: string): Promise<string[]> {
    const db = await this.getDb();
    const allKeys = (await db.getAllKeys(STORE_NAME)) as string[];
    if (!prefix) return allKeys;
    return allKeys.filter(k => typeof k === 'string' && k.startsWith(prefix));
  }

  async clear(): Promise<void> {
    const db = await this.getDb();
    await db.clear(STORE_NAME);
  }
}
